<template>
    <div class="sr">
        <!-- 左侧菜单 -->
        <div class="sr-left">
            <div v-if="multiple" class="sr-left-header">
                <Button size="small" @click="onAllClick">全部</Button>
            </div>
            <Tree
                ref="tree"
                :data="parkList"
                :isolation="!multiple"
                @on-select-change="selectItem"
            >
                <template #node="{ node, data }">
                    <span class="sr-it">
                        <span
                            class="sr-it-title"
                            :class="{
                                'is-selected': data.id === activeParentId,
                            }"
                            @click.stop="selectItem(node, data)"
                            >{{ data.title }}</span
                        >
                        <span
                            class="sr-it-check"
                            :class="{
                                'is-checked': data.checked,
                                'is-show': allowCheckParent,
                            }"
                            @click.stop="checkItem(node, data)"
                        >
                            <Icon
                                v-if="data.checked"
                                type="ios-checkmark-circle"
                                class="sr-it-check-icon is-checked"
                                :color="$theme.primaryColor"
                            ></Icon>
                            <Icon
                                v-else-if="data.indeterminate"
                                type="ios-remove-circle"
                                class="sr-it-check-icon is-checked"
                                :color="$theme.primaryColor"
                            ></Icon>
                            <Icon
                                v-else
                                type="ios-checkmark-circle"
                                class="sr-it-check-icon"
                                :color="$theme.primaryColor"
                            ></Icon>
                        </span>
                    </span>
                </template>
            </Tree>
        </div>
        <!-- 右侧列表 -->
        <div class="sr-right">
            <Table
                ref="mt"
                width="650"
                :columns="columns"
                :data="page.list"
                :loading="loading"
                :height="mtHeight"
                :highlight-row="!multiple"
                @on-current-change="onCurrentChange"
                @on-row-dblclick="onRowDbClick"
                @on-select="onSelect"
                @on-select-cancel="onSelectCancel"
                @on-select-all="onSelectAll"
                @on-select-all-cancel="onSelectAllCancel"
            />
            <!-- 底部分页 -->
            <table-pager
                :total.sync="page.total"
                :pageSize.sync="filter.pageSize"
                :pageNo.sync="filter.pageNo"
                @change="onHousePage"
            />
        </div>
    </div>
</template>
<script>
import Dict from '_u/dict'
import { deepCopy, type, listToTree } from '_u/helper'
import { mapCell } from '_c/grid/renderCell'

import { assetsTreeList, assetsHouseList } from '@/api/assets'

const PAGE_SIZE = 20

export default {
    name: 'SelectRoom',
    components: {},
    inject: {
        $modal: { default: null },
    },
    data() {
        return {
            loading: false,
            mtHeight: 430,
            page: {
                total: 0,
                list: [],
            },
            filter: {
                pageNo: 1,
                pageSize: PAGE_SIZE,
                parkId: '',
                floorId: '',
                floorNum: '',
                houseStatus: this.houseStatus,
            },
            dict: {
                parkType: Dict.NULL,
                houseStatus: Dict.NULL,
                meterState: this.$dict.NULL,
                bindState: this.$dict.NULL,
            },
            parkList: [],
            parkMap: {},
            activeParentId: '',
            selection: [],
        }
    },
    props: {
        // 是否允许选中园区，楼栋，楼层
        allowCheckParent: {
            type: Boolean,
            default: true,
        },
        // 是否允许多选
        multiple: {
            type: Boolean,
            default: false,
        },
        // 房间列表过滤
        // 1 空置；2 预定；3 租住；4 无效 ; 1,2 空置+预定；'' 所有
        houseStatus: {
            type: [String, Number],
            default: '1,2,3',
        },
        // 是否带出房间详情
        showDetail: {
            type: Boolean,
            default: false,
        },
        // 是否显示设备状态——(智能设备列表需要)
        showDevice: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        columns() {
            let list = [
                {
                    type: 'index',
                    title: '序号',
                    align: 'center',
                    width: 60,
                },
                {
                    key: 'houseNum',
                    title: '房间号',
                    align: 'left',
                    minWidth: 80,
                },
                {
                    key: 'houseStatus',
                    title: '房间状态',
                    align: 'left',
                    minWidth: 80,
                    render: mapCell(this.dict.houseStatus.toList()),
                },
                {
                    key: 'parkName',
                    title: '社区名称',
                    align: 'left',
                    minWidth: 100,
                },
                {
                    key: 'floorNameText',
                    title: '楼栋',
                    align: 'left',
                    minWidth: 80,
                },
            ]
            if (this.showDevice) {
                list.splice(3, 0, {
                    key: 'meterState',
                    title: '设备状态',
                    align: 'left',
                    minWidth: 100,
                    render: mapCell(this.dict.meterState.toList()),
                })
                list.splice(4, 0, {
                    key: 'bindState',
                    title: '绑定状态',
                    align: 'left',
                    minWidth: 100,
                    render: mapCell(this.dict.bindState.toList()),
                })
            }
            if (this.multiple) {
                list.unshift({
                    type: 'selection',
                    align: 'center',
                    width: 50,
                })
            }
            return list
        },
        selectionMap() {
            return this.selection.reduce((acc, v) => ((acc[v.id] = v), acc), {})
        },
    },
    methods: {
        fetch() {
            let dictArr = ['house_status', 'park_type']
            this.loading = true

            if (this.showDevice) {
                dictArr.push('meter_state')
                dictArr.push('bind_state')
            }

            Promise.all([
                assetsTreeList({ pageNo: 1, pageSize: 999, level: 3 }),
                Dict.get(dictArr),
            ]).then((args) => {
                var [res, dictGroup] = args

                Object.keys(this.dict).forEach((name) => {
                    this.dict[name] = dictGroup[name]
                })

                // 园区架构数据
                const list = res.data.list.sort((v) => v.name)
                listToTree(list, {
                    pId: 'pid',
                    level: 'treeLevel',
                    done: (tree, map, list) => {
                        list.forEach((v) => {
                            v.title = v.name
                            v.expand = false
                            v.checked = false
                            v.indeterminate = false
                            v.nodeKey = -1
                            // 园区 parkId
                            if (v.level == 1) {
                                v.expand = true
                                v.text = v.title
                            }
                            // 楼栋 floorId
                            else if (v.level === 2) {
                                var park = v.parent
                                if (park) {
                                    v.text = park.text + v.title
                                }
                            }
                            // 楼层 floorNum
                            else if (v.level === 3) {
                                var floor = v.parent
                                v.text = floor.text + v.title
                                v.id = floor.id + '_' + v.id // 楼层只有编号，因此需要造一个唯一id
                            }
                            // 默认选中
                            if (this.selectionMap[v.id]) {
                                var parentNode = v.parent
                                v.checked = true
                                // 递归展开
                                while (parentNode) {
                                    parentNode.expand = true
                                    parentNode = parentNode.parent
                                }
                            }
                        })

                        this.parkList = tree
                        this.parkMap = map
                    },
                })
                // 默认选中第一个园区
                if (this.filter.parkId === '' && this.parkList.length > 0) {
                    this.filter.parkId = this.parkList[0].id
                }
                // 查询房间
                this.fetchHouse()
            })
        },
        // 获取房间列表
        fetchHouse() {
            this.loading = true

            return assetsHouseList(this.filter)
                .then((res) => {
                    let list = res.data.page.list

                    this.page.list = this.normalizeHouseList(list)
                    this.page.total = res.data.page.totalCount - 0
                })
                .finally(() => {
                    this.loading = false
                })
        },
        // 房间数据标准化
        normalizeHouseList(list) {
            list.forEach((v) => {
                v.houseStatusText = this.dict.houseStatus.val(v.houseStatus)
                v.rent /= 100
                v.title = this.getHouseTitle(v)
                v.floorNameText = v.floorName
                v.level = 4
                // 默认选中
                if (this.multiple) {
                    if (this.selectionMap[v.id]) {
                        v._checked = true
                    }
                } else {
                    if (this.selectionMap[v.id]) {
                        v._highlight = true
                    }
                }
            })
            // 房间状态为4，空置与预定的显示
            if (this.houseStatus === '4') {
                list = list.filter(
                    (v) => v.houseStatus === '1' || v.houseStatus === '2'
                )
            }
            return list
        },
        // 获取房间标题
        getHouseTitle(room) {
            let str = ''
            str += room.parkName ? room.parkName : ''
            str += room.floorName ? room.floorName : ''
            str += room.houseNum ? room.houseNum : ''
            return str
        },
        // 获取格式化数据
        getFormatData(item) {
            var data = {
                id: item.id,
                title: item.text || item.title,
                parkId: '',
                parkName: '',
                floorId: '',
                floorName: '',
                floorNum: '',
                houseId: '',
                houseNum: '',
                level: item.level,
            }

            if (item.level === 1) {
                data.parkId = item.id
                data.parkName = item.title
            } else if (item.level === 2) {
                data.parkId = item.parent.id
                data.parkName = item.parent.title
                data.floorId = item.id
                data.floorName = item.title
            } else if (item.level === 3) {
                var [floorId, floorNum] = item.id.split('_')
                data.parkId = item.parent.parent.id
                data.parkName = item.parent.parent.title
                data.floorId = floorId
                data.floorName = item.parent.title
                data.floorNum = floorNum
            } else if (item.level === 4) {
                data.parkId = item.parkId
                data.parkName = item.parkName
                data.floorId = item.floorId
                data.floorName = item.floorName
                data.floorNum = item.floorNum
                data.houseId = item.id
                data.houseNum = item.houseNum
            }

            return data
        },
        // TreeItem更改
        selectItem(node, item) {
            this.activeParentId = item.id
            Object.assign(this.filter, this.getFormatData(item))
            this.filter.pageNo = 1
            // 只限选择当前页房间
            this.clearCurrentHouse()
            this.fetchHouse()
        },
        // TreeItem选中
        checkItem(node, item) {
            var checked = !item.checked

            // 多选模式
            if (this.multiple) {
                this.$refs.tree.handleCheck({ checked, nodeKey: item.nodeKey })
                this.selection = this.populateSelection()
            }
            // 单选模式
            else {
                if (checked) {
                    this.$refs.mt.clearCurrentRow()
                    var lastSelect = this.selection[0]
                    if (lastSelect && this.parkMap[lastSelect.id]) {
                        this.parkMap[lastSelect.id].checked = false
                    }
                    this.selection = [this.getFormatData(item)]
                } else {
                    this.selection = []
                }
                item.checked = checked
            }
        },
        // 清空当前选中的房间
        clearCurrentHouse() {
            this.selection = this.selection.filter((v) => v.level < 4)
        },
        // 收集已选择项
        populateSelection() {
            var selection = [...this.selection.filter((v) => v.level > 3)] // 已选中的房间
            selection.push(
                ...this.$refs.tree
                    .getCheckedNodes()
                    .filter((v) => v.isEnd) // 到末级
                    .map((v) => this.getFormatData(v))
            )

            return selection
        },
        // 左侧-全部勾选
        onAllClick() {
            var checked = !this.parkList.every((v) => v.checked)

            this.parkList.forEach((v) => {
                this.$refs.tree.handleCheck({
                    checked: checked,
                    nodeKey: v.nodeKey,
                })
            })

            this.selection = this.populateSelection()
        },
        // 房间分页事件
        onHousePage() {
            this.clearCurrentHouse()
            this.fetchHouse()
        },
        // 房间单选
        onCurrentChange(row, oldRow) {
            if (!row) return
            this.activeParentId = ''

            var data = {
                contractId: row.contractId,
            }
            if (this.showDetail) {
                data = deepCopy(row)
            }
            // 清除park单选
            var lastSelect = this.selection[0]
            if (lastSelect && this.parkMap[lastSelect.id]) {
                this.parkMap[lastSelect.id].checked = false
            }
            this.selection = [Object.assign(data, this.getFormatData(row))]
        },
        // 房间双击
        onRowDbClick(row) {
            if (this.multiple) return

            this.onCurrentChange(row)

            // this.activeParentId = ''
            // if (type(selection) != 'array') {
            //     selection = [selection]
            // }
            // let detail = {}
            // if (this.showDetail) {
            //     detail = deepCopy(selection[0])
            // }
            // this.selection = [
            //     Object.assign(detail, {
            //         houseId: selection[0].id,
            //         title: selection[0].title,
            //         contractId: selection[0].contractId,
            //         level: 4,
            //         parkId: '',
            //         floorId: '',
            //         floorNum: '',
            //     }),
            // ]
            // const { parkId, floorId, floorNum, houseId } = this.selection[0]
            // Object.assign(this.parentFilter, {
            //     parkId,
            //     floorId,
            //     floorNum,
            //     houseId,
            // })
            this.$modal.$emit('lookup-close', this.selection)
        },
        // 多选模式-房间选择
        onSelect(selection, row) {
            this.selection.push(this.getFormatData(row))
        },
        // 多选模式-房间取消选择
        onSelectCancel(selection, row) {
            var index = this.selection.findIndex((v) => v.id === row.id)

            if (index >= 0) {
                this.selection.splice(index, 1)
            }
        },
        // 多选模式-房间全部选择
        onSelectAll(selection) {
            selection.forEach((v) => {
                if (!this.selectionMap[v.id]) {
                    this.selection.push(this.getFormatData(v))
                }
            })
        },
        // 多选模式-房间全部取消
        onSelectAllCancel(selection) {
            this.page.list.forEach((v) => this.onSelectCancel([], v))
        },
    },
    created() {
        this.$modal.$on('lookup-open', (data) => {
            // 上一次选中项
            this.selection = data.map((v) => v.option)

            // 打开选择框
            this.$modal.beforeEnter((next) => {
                Promise.all([this.fetch()]).finally(() => {
                    next()
                })
            })

            // 关闭选择框
            this.$modal.onBtnClick('confirm', (next, options) => {
                console.log('selection', this.selection[0])
                if (this.selection.length) {
                    this.$modal.$emit('lookup-close', this.selection)
                } else {
                    this.$Message.warning('请选择房源')
                    options.closeModal = false
                }

                next()
            })
        })
    },
}
</script>

<style lang="less" scoped>
.sr {
    height: 100%;
    width: 100%;
    display: flex;

    &-left {
        position: relative;
        height: 100%;
        width: 230px;
        overflow-y: auto;

        &-header {
            padding: 12px 0 0 4px;
        }
    }
    &-right {
        height: 100%;
    }

    &-it {
        display: inline-block;
        width: 100%;
        line-height: 25px;

        &-title {
            display: inline-block;
            width: 100px;
            height: 25px;
            border-radius: 20px;
            padding-left: 10px;
            cursor: pointer;

            &.is-selected {
                background: #f7f7f7;
            }
        }
        &-check {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            float: right;
            margin-right: 10px;
            border: 1px solid #cccccc;
            position: relative;
            top: 4px;
            cursor: pointer;
            display: none;

            &.is-checked {
                border-color: rgba(0, 0, 0, 0) !important;
            }
            &.is-show {
                display: inline-block !important;
            }

            &-icon {
                position: relative;
                top: -7px;
                left: -2px;
                display: none;
                font-size: 20px;

                &.is-checked {
                    display: inline-block !important;
                }
            }
        }
    }
}
</style>
